import { LOCKED_CODES, parsePaymentsValidationError } from '@/api'
import { Button, Input } from '@/ui-kit'
import CheckBox from '@/ui-kit/components/Checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/ui-kit/components/Dialog'
import { formatNumber } from '@/utils'
import { Trans, useLingui } from '@lingui/react/macro'
import { useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useEffect, useRef, useState } from 'react'
import { type UseControllerReturn, useController, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { FormSelect } from '../../missions/components/FormSelect'
import {
  type MethodId,
  PAYOUT_METHODS,
  PAYOUT_METHOD_OPTIONS,
  type PayoutMethods,
  type Settings,
  buildPayPalAuthorizeUrl,
  consumeStash,
  createStash,
  isMethodEnabled,
  isStripeAccountPayable,
  resolveMethodMin,
  roundUpUsdToCents,
  stripeAccountNeedsAction,
  useCreateStripeAccount,
  useCreateStripeOnboardingLink,
  useFetchPayoutMethods,
  useFetchSettings,
  useFetchStripeAccount,
  useFetchUserBalance,
  useRequestPayout
} from '../api'
import { TremendousOptionsLink } from './TremendousOptionsLink'

type WithdrawModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Shows the "PayPal sign-in was cancelled" notice when the user backed out of the consent screen. */
  consentCancelledNotice?: boolean
  initialPaymentMethod?: MethodId
}

// RHF field names for the two method-specific inputs known today. Both controllers are registered
// up front (RHF wants static names); each validator is gated on the active method, so an inactive
// field never blocks submission (UK1).
type FieldName = 'walletAddress' | 'recipientEmail'
type WithdrawFormValues = {
  amount: string
  walletAddress: string
  recipientEmail: string
  paymentMethod: string // a MethodId (functional) or an ANNOUNCED_METHODS value (coming soon)
}

const DEFAULT_PAYMENT_METHOD: MethodId = 'crypto-solana-usdc'
const DEFAULT_FORM_VALUES: WithdrawFormValues = {
  amount: '',
  walletAddress: '',
  recipientEmail: '',
  paymentMethod: DEFAULT_PAYMENT_METHOD
}

const validateAmount = (value: string, available: number, minPayout: number, maxAmount?: number): string | true => {
  const numericAmount = Number(value)
  if (value === '' || !Number.isFinite(numericAmount) || numericAmount <= 0) {
    return LOCKED_CODES.AMOUNT_NOT_POSITIVE
  }
  if (numericAmount < minPayout) {
    return `Amount must be at least $${formatNumber(minPayout, 2)}`
  }
  if (maxAmount != null && numericAmount > maxAmount) {
    return `Amount must be at most $${formatNumber(maxAmount, 2)}`
  }
  if (numericAmount > available) {
    return LOCKED_CODES.INSUFFICIENT_BALANCE
  }
  return true
}

// Tremendous's tile visibility (enabled and not paused) comes from the per-user endpoint instead of
// `settings`; every other method goes through `isMethodEnabled`, which already excludes paused ones.
const isTileEnabled = (settings: Settings | undefined, pm: PayoutMethods | undefined, id: MethodId): boolean =>
  id === 'tremendous' ? pm?.tremendous.enabled === true && pm.tremendous.paused !== true : isMethodEnabled(settings, id)

export const WithdrawModal = ({
  open,
  onOpenChange,
  consentCancelledNotice,
  initialPaymentMethod
}: WithdrawModalProps) => {
  const { t } = useLingui()
  const { data: balance } = useFetchUserBalance()
  const { data: settings } = useFetchSettings()
  const { data: payoutMethods, isFetching: isPayoutMethodsFetching } = useFetchPayoutMethods(open)
  const { mutateAsync: requestPayout } = useRequestPayout()
  const { mutateAsync: createStripeAccount } = useCreateStripeAccount()
  const { mutateAsync: createStripeOnboardingLink } = useCreateStripeOnboardingLink()
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isWithdrawalConfirmed, setIsWithdrawalConfirmed] = useState(false)
  const { control, watch, handleSubmit, setValue, setError, clearErrors, reset, trigger, getValues } =
    useForm<WithdrawFormValues>({
      defaultValues: DEFAULT_FORM_VALUES,
      mode: 'onTouched',
      reValidateMode: 'onChange'
    })

  const paymentMethod = watch('paymentMethod')
  const { data: stripeAccount, isFetching: isStripeAccountFetching } = useFetchStripeAccount(
    open && paymentMethod === 'stripe-connect' && settings?.stripeConnectEnabled === true
  )
  // The active functional method, or undefined when an announced ("coming soon") stub is selected.
  const def = PAYOUT_METHODS[paymentMethod as MethodId]
  const tremendousAvailability = paymentMethod === 'tremendous' ? payoutMethods?.tremendous : undefined
  const isComingSoon = !def
  const isStripe = paymentMethod === 'stripe-connect'
  const isStripeFeatureDisabled = isStripe && !settings?.stripeConnectEnabled
  const isStripeReady = isStripeAccountPayable(stripeAccount)
  const isStripeActionRequired = isStripe && stripeAccountNeedsAction(stripeAccount)
  const isStripeReviewing = isStripe && !!stripeAccount && !isStripeReady && !isStripeActionRequired
  const isStripeSetupFlow = isStripe && !isStripeReady && !isStripeReviewing
  // The single branch point for the PayPal OAuth-consent ceremony (settings-selected). When false
  // — `paypalPaymentType` absent or 'email' — every path below is byte-identical to today (IV4).
  const isOauthCeremony = !!def?.oauthRedirect && settings?.paypalPaymentType === 'oauth'

  const available = balance?.availableUsd ?? 0
  const minPayout =
    tremendousAvailability?.minPayoutUsd != null
      ? roundUpUsdToCents(tremendousAvailability.minPayoutUsd)
      : resolveMethodMin(settings, def)
  const maxAmountUsd =
    typeof tremendousAvailability?.maxPayoutUsd === 'number' ? tremendousAvailability.maxPayoutUsd : def?.maxAmountUsd

  // Method-blind field validator: find the field on the active method and run its registry rule.
  // A field that isn't part of the active method returns `true` (never blocks) — that's how the
  // always-registered controllers stay inert for the non-selected method (UK1, PC1).
  const validateField = (fieldKey: FieldName, value: string): string | true => {
    // Ceremony mode collects no destination fields — the always-registered controllers must stay
    // inert here (and `fieldsValid` resolve to true) or the submit button never enables.
    if (isOauthCeremony) return true
    const fieldDef = def?.fields.find((f) => f.key === fieldKey)
    if (!fieldDef) return true
    const result = fieldDef.validate(value.trim())
    return result === true ? true : (LOCKED_CODES[result] ?? result)
  }

  const amountField = useController({
    name: 'amount',
    control,
    rules: {
      validate: (value) => validateAmount(value, available, minPayout, maxAmountUsd)
    }
  })
  const walletAddressField = useController({
    name: 'walletAddress',
    control,
    rules: {
      validate: (value) => validateField('walletAddress', value)
    }
  })
  const recipientEmailField = useController({
    name: 'recipientEmail',
    control,
    rules: {
      validate: (value) => validateField('recipientEmail', value)
    }
  })
  const fieldControllers: Record<FieldName, UseControllerReturn<WithdrawFormValues, FieldName>> = {
    walletAddress: walletAddressField,
    recipientEmail: recipientEmailField
  }

  // Gates the initial-pick effect below to once per open: Tremendous availability now arrives
  // asynchronously (payoutMethods), so the effect keeps re-running on that data settling — this
  // ref stops it from re-applying `initialPaymentMethod` over a choice the user already made.
  const pickAppliedRef = useRef(false)

  useEffect(() => {
    if (!open) {
      reset(DEFAULT_FORM_VALUES)
      setFormError(null)
      setIsSubmitting(false)
      setIsWithdrawalConfirmed(false)
      pickAppliedRef.current = false
    }
  }, [open, reset])

  // Resume after the PayPal consent redirect: on the `open` rising edge, consume the ceremony
  // stash (IV5) and seed the form back to where the user left off. The close-reset effect above
  // only acts when `open` is false, so seeding on open and clearing on close never fight.
  // Tremendous's pick additionally waits on `payoutMethods` to settle (`isPayoutMethodsFetching`)
  // and every other method waits on `settings`, so this can re-run a few times per open before
  // `pickAppliedRef` locks it (IV3).
  useEffect(() => {
    if (!open || pickAppliedRef.current) return
    const stash = consumeStash()
    if (stash) {
      setValue('paymentMethod', 'paypal')
      setValue('amount', stash.amountUsd)
      pickAppliedRef.current = true
      return
    }
    if (!initialPaymentMethod) {
      pickAppliedRef.current = true
      return
    }
    if (initialPaymentMethod === 'tremendous') {
      if (isPayoutMethodsFetching) return
    } else if (settings === undefined) {
      return
    }
    setValue(
      'paymentMethod',
      isTileEnabled(settings, payoutMethods, initialPaymentMethod) ? initialPaymentMethod : DEFAULT_PAYMENT_METHOD
    )
    pickAppliedRef.current = true
  }, [initialPaymentMethod, open, setValue, settings, payoutMethods, isPayoutMethodsFetching])

  // `minPayout` is now per-method, so switching methods (or settings arriving) changes what the
  // amount is checked against. Re-validate the already-entered amount when the min changes so the
  // displayed error can't drift out of sync with `canSubmit` (IV2). Read via `getValues` (not the
  // watched `amount`) so this fires only on min changes, never showing a min error mid-first-keystroke.
  useEffect(() => {
    if (getValues('amount') !== '') void trigger('amount')
  }, [minPayout, getValues, trigger])

  const amount = watch('amount')
  const numericAmount = Number(amount)
  const fieldValues: Record<string, string> = {
    walletAddress: watch('walletAddress'),
    recipientEmail: watch('recipientEmail')
  }
  // AMOUNT_INPUT_MASK is now per-method (keyed on the active precision) instead of a static
  // module-load const carried from BS1. Both methods are 2-dp today; this fixes the seam.
  const amountInputMask = new RegExp(`^\\d*\\.?\\d{0,${def?.decimalPrecision ?? 2}}$`)
  const amountClientValidation = validateAmount(amount, available, minPayout, maxAmountUsd)
  // The amount Input clears its RHF error on every keystroke (server-error reset) and `onTouched`
  // defers client errors until blur — so a below-min amount would disable the button with no reason.
  // Fall back to the live client message whenever there's no RHF error and the entered amount is invalid.
  const amountErrorText =
    amountField.fieldState.error?.message ??
    (amount !== '' && amountClientValidation !== true ? amountClientValidation : undefined)
  const fieldsValid =
    !!def && def.fields.every((f) => validateField(f.key as FieldName, fieldValues[f.key] ?? '') === true)
  // Registry labels are MessageDescriptors (FU1); resolve to strings for FormSelect (label: string).
  const paymentMethodOptions = PAYOUT_METHOD_OPTIONS.filter(
    (o) => o.comingSoon || isTileEnabled(settings, payoutMethods, o.value as MethodId)
  ).map((o) => ({
    value: o.value,
    label: t(o.label),
    comingSoon: o.comingSoon
  }))
  const selectedPaymentMethodLabel = paymentMethodOptions.find((method) => method.value === paymentMethod)?.label

  const onMaxClick = () => {
    if (available > 0) {
      const maxValue = Math.min(available, maxAmountUsd ?? available)
      setValue('amount', maxValue.toFixed(2), { shouldTouch: true, shouldValidate: true })
      clearErrors('amount')
    }
  }

  const canWithdraw =
    !isStripeFeatureDisabled &&
    !isStripeReviewing &&
    !(isStripe && isStripeAccountFetching) &&
    !isSubmitting &&
    !!def &&
    amount !== '' &&
    amountClientValidation === true &&
    fieldsValid &&
    (!def.requiresConfirmation || isWithdrawalConfirmed)
  const canStartStripeSetup =
    isStripeSetupFlow &&
    !isStripeReviewing &&
    !isStripeFeatureDisabled &&
    !isStripeAccountFetching &&
    !isSubmitting &&
    !!def
  const canSubmit = isStripeSetupFlow ? canStartStripeSetup : canWithdraw

  const onWithdrawClick = handleSubmit(async (data) => {
    // Capture the active method at submit time: the request is async and the modal stays open on
    // error, so a picker switch mid-flight must not retarget the methodInput build or the
    // server-error map (below) onto a different method's fields.
    const activeDef = def
    if (!activeDef) return
    setIsSubmitting(true)
    try {
      const collected = Object.fromEntries(
        activeDef.fields.map((f) => [f.key, (data[f.key as FieldName] ?? '').trim()])
      )
      const methodInput = { ...collected, ...activeDef.staticMethodInput }
      await requestPayout({
        method: activeDef.id,
        amountUsd: numericAmount.toString(),
        methodInput
      })
      onOpenChange(false)
      toast.success(t`Payout requested`)
    } catch (err) {
      // Any rejection on a Tremendous submit can mean the endpoint's picture went stale (e.g. the
      // tile got disabled/paused mid-flight) — refetch it so the form reflects the current state.
      if (activeDef.id === 'tremendous') void queryClient.invalidateQueries({ queryKey: ['payout-methods'] })
      if (axios.isAxiosError(err)) {
        const details = (err.response?.data as { error?: { details?: unknown[] } })?.error?.details
        if (Array.isArray(details) && details.some((d) => (d as { code?: string }).code === 'BELOW_MIN_AMOUNT')) {
          queryClient.invalidateQueries({ queryKey: ['settings'] })
        }
      }
      const parsed = parsePaymentsValidationError(err)
      if (parsed) {
        // Map the wire field path (e.g. 'methodInput.recipientEmail') back to the RHF field name,
        // derived from the active method's fields. 'amountUsd' is method-independent.
        const serverFieldMap: Record<string, keyof WithdrawFormValues> = {
          amountUsd: 'amount',
          ...Object.fromEntries(activeDef.fields.map((f) => [f.serverField, f.key]))
        }
        for (const [serverField, message] of Object.entries(parsed.fieldErrors)) {
          const rhfName = serverFieldMap[serverField]
          if (rhfName) {
            setError(rhfName, { type: 'server', message })
          }
        }
        setFormError(parsed.formError)
      } else {
        toast.error(t`Couldn't request payout — try again.`)
      }
    } finally {
      setIsSubmitting(false)
    }
  })

  const onStripeSetupClick = async () => {
    if (!canStartStripeSetup) return
    setIsSubmitting(true)
    setFormError(null)
    try {
      if (!stripeAccount) {
        await createStripeAccount()
      }
      const link = await createStripeOnboardingLink()
      window.location.assign(link.url)
    } catch (err) {
      const parsed = parsePaymentsValidationError(err)
      if (parsed) {
        setFormError(parsed.formError)
      } else {
        toast.error(t`Couldn't continue Stripe onboarding — try again.`)
      }
      setIsSubmitting(false)
    }
  }

  // Ceremony submit: persist the one-withdraw stash (state nonce + idempotency key are minted
  // inside createStash — IV1/IV2) and hand off to the PayPal consent screen. No RHF submit —
  // ceremony mode collects no destination fields. `createStash` THROWS when sessionStorage
  // rejects the write; redirecting without a persisted stash would dead-end after consent, so we
  // surface the error and stay put. `isSubmitting` intentionally stays true after a successful
  // kick-off — the page is navigating away.
  const onContinueWithPayPalClick = () => {
    if (!canSubmit) return
    setIsSubmitting(true)
    try {
      const stash = createStash(amount)
      window.location.assign(buildPayPalAuthorizeUrl(stash.stateNonce))
    } catch (err) {
      console.warn('Failed to start the PayPal withdraw ceremony', err)
      setIsSubmitting(false)
      toast.error(t`Couldn't request payout — try again.`)
    }
  }

  const onPrimaryClick = isStripeSetupFlow
    ? onStripeSetupClick
    : isOauthCeremony
      ? onContinueWithPayPalClick
      : onWithdrawClick
  const primaryLabel = isOauthCeremony ? (
    <Trans>Continue with PayPal</Trans>
  ) : isStripeSetupFlow ? (
    <Trans>Continue with Stripe</Trans>
  ) : isStripeReviewing ? (
    <Trans>Under review</Trans>
  ) : (
    <Trans>Withdraw</Trans>
  )

  const updateField = (name: keyof WithdrawFormValues, value: string) => {
    // Confirmation is per-destination: switching methods must drop a prior tick so the user can't
    // submit a new destination on a stale confirm (the CheckBox is keyed on paymentMethod below so
    // its uncontrolled visual state remounts in step with this reset).
    if (name === 'paymentMethod') {
      setIsWithdrawalConfirmed(false)
    }
    setValue(name, value, { shouldValidate: true })
  }

  // A method can become unavailable (paused, disabled) after it was picked — via a later fetch of
  // `settings` or `payoutMethods` — so fall back to the default rather than leaving the form pointed
  // at a tile the picker no longer shows. Gated on the selected method's availability source having
  // loaded (`payoutMethods` for Tremendous, `settings` otherwise) so it never fires on the loading state.
  useEffect(() => {
    const sourceLoaded = paymentMethod === 'tremendous' ? payoutMethods !== undefined : settings !== undefined
    if (open && sourceLoaded && !isTileEnabled(settings, payoutMethods, paymentMethod as MethodId)) {
      updateField('paymentMethod', DEFAULT_PAYMENT_METHOD)
    }
  }, [open, paymentMethod, payoutMethods, settings])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[480px]!"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            <Trans>Withdraw</Trans>
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2 flex flex-col gap-5">
          {formError && (
            <div className="rounded-lg border border-solid border-red-500/30 bg-red-500/10 px-3 py-2 text-red-500">
              {formError}
            </div>
          )}

          {consentCancelledNotice && (
            <div className="border-orange/20 bg-orange/10 rounded-lg border border-solid px-3 py-3">
              <div className="text-body-2">
                <Trans>PayPal sign-in was cancelled — your withdrawal wasn’t submitted.</Trans>
              </div>
            </div>
          )}

          <FormSelect
            value={watch('paymentMethod')}
            onChange={(value) => updateField('paymentMethod', value)}
            label={t`Payment method`}
            options={paymentMethodOptions}
            placeholder={t`Select payment method`}
          />

          {paymentMethod === 'tremendous' && (
            <TremendousOptionsLink minPayout={minPayout} maxAmountUsd={maxAmountUsd} />
          )}

          {isComingSoon && (
            <div className="border-orange/20 bg-orange/10 rounded-lg border border-solid px-3 py-3">
              <div className="text-body-2">
                <Trans>
                  <strong>{selectedPaymentMethodLabel}</strong> support coming soon
                </Trans>
              </div>
              <div className="text-body-2 text-gray-80 mt-2">
                <Trans>
                  This payment method is not available yet. Please use another available option for now.
                </Trans>{' '}
              </div>
            </div>
          )}

          {def && isStripeSetupFlow && (
            <div className="border-orange/20 bg-orange/10 rounded-lg border border-solid px-3 py-3">
              <div className="text-body-2 text-gray-80">
                {isStripeActionRequired ? (
                  <Trans>Stripe needs more information before payouts can be sent.</Trans>
                ) : (
                  <Trans>
                    Set up Stripe before withdrawing. You can request a payout after Stripe approves your account.
                  </Trans>
                )}
              </div>
            </div>
          )}

          {def && isStripeReviewing && (
            <div className="border-orange/20 bg-orange/10 rounded-lg border border-solid px-3 py-3">
              <div className="text-body-2 text-gray-80">
                <Trans>
                  Stripe is reviewing your account. You can request a payout after Stripe approves your account.
                </Trans>
              </div>
            </div>
          )}

          {def && !isStripeSetupFlow && !isStripeReviewing && (
            <>
              <div className="flex flex-col gap-2">
                <div className="relative">
                  <Input
                    placeholder={t`Amount`}
                    value={amountField.field.value}
                    onChange={(value) => {
                      if (value !== '' && !amountInputMask.test(value)) return
                      amountField.field.onChange(value)
                      setFormError(null)
                      clearErrors('amount')
                    }}
                    isError={!!amountErrorText}
                    errorText={amountErrorText}
                    defaultPristine={false}
                  />
                  <div className="absolute top-2 right-2 z-1">
                    <Button
                      size="small"
                      isDisabled={available < minPayout}
                      className="hover:border-orange! border border-solid border-gray-100! bg-transparent! px-4! hover:bg-none!"
                      onClick={onMaxClick}
                    >
                      <Trans>Max</Trans>
                    </Button>
                  </div>
                  <div className="text-body-3 text-gray-90 mt-1 flex justify-between px-2">
                    <div>
                      <Trans>Available: ${formatNumber(available, 2)}</Trans>
                    </div>
                    <div>
                      <Trans>Min withdrawal: ${formatNumber(minPayout, 2)}</Trans>
                    </div>
                    {maxAmountUsd != null && (
                      <div>
                        <Trans>Max withdrawal: ${formatNumber(maxAmountUsd, 2)}</Trans>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Ceremony mode collects no destination fields — PayPal supplies the account on its side. */}
              {!isOauthCeremony &&
                def.fields.map((f) => {
                  const ctrl = fieldControllers[f.key as FieldName]
                  if (!ctrl) return null
                  return (
                    <div key={f.key} className="flex flex-col gap-2">
                      <Input
                        placeholder={t(f.placeholder)}
                        value={ctrl.field.value}
                        onChange={(value) => {
                          ctrl.field.onChange(value)
                          setFormError(null)
                          clearErrors(f.key as FieldName)
                        }}
                        isError={!!ctrl.fieldState.error}
                        errorText={ctrl.fieldState.error?.message}
                        defaultPristine={false}
                      />
                    </div>
                  )
                })}
              <div className="border-orange/20 bg-orange/10 rounded-lg border border-solid px-3 py-3">
                <div className="text-body-2 text-gray-80">
                  <Trans>Withdrawal requests cannot be canceled or reversed.</Trans>
                </div>
              </div>
              {def.requiresConfirmation && (
                <>
                  <div className="text-body-2 text-gray-80">
                    {/* `oauthRedirect` methods always define `oauthConfirmCopy` (registry contract). */}
                    {isOauthCeremony && def.oauthConfirmCopy
                      ? t(def.oauthConfirmCopy(amount))
                      : isStripe && stripeAccount
                        ? t`Send $${amount} via Stripe to account ${stripeAccount.stripeAccountId}?`
                        : t(def.confirmCopy(fieldValues, amount))}
                  </div>
                  <CheckBox
                    key={paymentMethod}
                    defaultChecked={isWithdrawalConfirmed}
                    onChange={(value) => {
                      setIsWithdrawalConfirmed(Boolean(value))
                    }}
                    className="text-gray-70"
                    label={
                      isOauthCeremony
                        ? t`I confirm the amount is correct`
                        : isStripe
                          ? t`I confirm the amount and connected Stripe account are correct`
                          : paymentMethod === 'tremendous'
                            ? t`I confirm the amount is correct`
                            : t`I confirm amount and destination address are correct`
                    }
                  />
                </>
              )}
            </>
          )}

          <div className="mt-2 flex gap-3 max-md:flex-col-reverse">
            <Button
              variant="bordered-gray"
              className="flex-1"
              onClick={() => onOpenChange(false)}
              isDisabled={isSubmitting}
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button className="flex-1" onClick={onPrimaryClick} isDisabled={!canSubmit} isLoading={isSubmitting}>
              <span className="colorWhite">{primaryLabel}</span>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
